import { Link } from "react-router-dom";
import { BookOpen, Heart, NotebookPen, ScrollText } from "lucide-react";
import { COMMON_PRAYERS } from "@/lib/constants";

const Community = () => {
    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
            <span className="eyebrow">Parishioner Space</span>
            <h1 className="heading-display mt-2">Community Hub</h1>
            <p className="mt-4 max-w-2xl text-maroon-700/85">
                A space for parishioners to nourish their faith, read today&apos;s Mass, pray together,
                explore the great prayers of the Church, and share your own reflections on the blog.
            </p>

            {/* Quick-access cards, maroon / gold treatment */}
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                <Link
                    to="/daily-readings"
                    data-testid="community-card-readings"
                    className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gold-300/10 blur-2xl transition-opacity group-hover:opacity-100" />
                    <div className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300 group-hover:bg-gold-300 group-hover:text-maroon-900 transition-colors">
                        <BookOpen size={20} />
                    </div>
                    <h3 className="relative text-lg text-cream-50 mt-4">Daily Mass Readings</h3>
                    <p className="relative text-xs text-cream-50/75 mt-1">Today&apos;s full Liturgy of the Word, right here on the parish website.</p>
                    <span className="relative mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gold-300 group-hover:text-gold-200">
                        Open today&apos;s readings →
                    </span>
                </Link>
                <Link
                    to="/prayer-wall"
                    data-testid="community-card-prayer-wall"
                    className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gold-300/10 blur-2xl" />
                    <div className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300 group-hover:bg-gold-300 group-hover:text-maroon-900 transition-colors">
                        <Heart size={20} />
                    </div>
                    <h3 className="relative text-lg text-cream-50 mt-4">Prayer Intentions</h3>
                    <p className="relative text-xs text-cream-50/75 mt-1">Submit your intention and our community will pray for you.</p>
                    <span className="relative mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gold-300 group-hover:text-gold-200">Open Prayer Wall →</span>
                </Link>
                <a
                    href="#common-prayers"
                    data-testid="community-card-prayers"
                    className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gold-300/10 blur-2xl" />
                    <div className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300 group-hover:bg-gold-300 group-hover:text-maroon-900 transition-colors">
                        <ScrollText size={20} />
                    </div>
                    <h3 className="relative text-lg text-cream-50 mt-4">Common Catholic Prayers</h3>
                    <p className="relative text-xs text-cream-50/75 mt-1">Our Father, Hail Mary, Apostles&apos; Creed, Memorare and more.</p>
                    <span className="relative mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gold-300 group-hover:text-gold-200">Open prayers ↓</span>
                </a>
                <Link
                    to="/blogs"
                    data-testid="community-card-blogs"
                    className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                    <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gold-300/10 blur-2xl" />
                    <div className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300 group-hover:bg-gold-300 group-hover:text-maroon-900 transition-colors">
                        <NotebookPen size={20} />
                    </div>
                    <h3 className="relative text-lg text-cream-50 mt-4">Parishioner Blogs</h3>
                    <p className="relative text-xs text-cream-50/75 mt-1">Reflections, testimonies and articles by our parishioners.</p>
                    <span className="relative mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gold-300 group-hover:text-gold-200">Read &amp; write blogs →</span>
                </Link>
            </div>

            {/* Common Catholic Prayers, kept on this page as an anchor list */}
            <section id="common-prayers" className="mt-20 scroll-mt-20">
                <span className="eyebrow">Traditional Prayers</span>
                <h2 className="heading-section mt-1">Common Catholic Prayers</h2>
                <p className="mt-3 max-w-2xl text-maroon-700/85">
                    The treasury of the Church&apos;s prayers, to be said by heart, with family, or in moments of quiet.
                </p>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                    {COMMON_PRAYERS.map((p) => (
                        <article
                            key={p.slug}
                            data-testid={`prayer-${p.slug}`}
                            className="rounded-2xl border border-maroon-100 bg-cream-50 p-5 hover:bg-white transition-colors"
                        >
                            <h3 className="text-xl text-maroon-800">{p.name}</h3>
                            <div className="gold-divider my-3 opacity-50" />
                            <pre className="text-sm text-maroon-700/90 leading-relaxed whitespace-pre-wrap font-sans">
                                {p.text}
                            </pre>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Community;
